import { useState } from "react";
import { Link } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { fetchWithCsrf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ApiSuccess, ApiError } from "@/types/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const requestReset = useMutation<ApiSuccess<null>, ApiError, string>({
    mutationFn: async (email) => {
      return fetchWithCsrf("/api/passwords", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    },
    onSuccess: () => {
      setSubmitted(true);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    requestReset.mutate(email);
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md space-y-8 text-center">
          <div>
            <h1 className="text-2xl font-bold uppercase tracking-[0.2em] mb-2">Cobar</h1>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Check your email</p>
          </div>

          <div className="bg-[#191919] border border-[#616161]/20 rounded-lg p-8">
            <p className="text-sm text-muted-foreground">
              If an account with that email exists, we've sent password reset
              instructions.
            </p>
          </div>

          <Link to="/login" className="text-xs font-medium uppercase tracking-wider text-primary hover:underline">
            Return to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold uppercase tracking-[0.2em] mb-2">Cobar</h1>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Forgot your password?</p>
        </div>

        <div className="bg-[#191919] border border-[#616161]/20 rounded-lg p-8">
          {requestReset.isError && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm mb-6">
              {requestReset.error?.message || "An error occurred"}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                autoComplete="email"
                required
                placeholder="you@example.com"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={requestReset.isPending}
            >
              {requestReset.isPending ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Remember your password?{" "}
          <Link
            to="/login"
            className="text-primary hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
